// Avaliacao da foto do produto pro catalogo — Onda Avaliacao de Foto.
// Usado na aba Catalogo do ProdutoForm. Avalia (tecnico + Gemini Vision) e,
// quando da, oferece a otimizacao automatica (pad quadrado 1200x1200 + WebP).
import { useState } from 'react';
import { Camera, Wand2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

export type VereditoFoto = 'boa' | 'refazer' | 'sem_foto';

export interface TecnicoFoto {
  largura: number;
  altura: number;
  formato: string;
  bytes: number;
  resolucao_critica: boolean;
  resolucao_baixa: boolean;
  nao_quadrada: boolean;
  pesada: boolean;
  formato_ineficiente: boolean;
  pouco_conteudo: boolean;
  ocupacao: number;
  otimizavel: boolean;
  problemas: string[];
}

export interface VisualFoto {
  nota: number;
  parecer: string;
  problemas: string[];
  precisa_refazer: boolean;
}

export interface AvaliacaoFoto {
  veredito: VereditoFoto;
  tecnico: TecnicoFoto | null;
  visual: VisualFoto | null;
}

// Rotulo + cor + icone de cada veredito. Cor acompanhada de texto e forma de
// icone distinta (circulo / triangulo / X) — seguro pra daltonismo.
export const VEREDITO_FOTO: Record<VereditoFoto, { rotulo: string; cor: string }> = {
  boa: { rotulo: 'Foto boa', cor: '#16a34a' },
  refazer: { rotulo: 'Refazer', cor: '#dc2626' },
  sem_foto: { rotulo: 'Sem foto', cor: '#9ca3af' },
};

export function IconeVeredito({ veredito, size = 16 }: { veredito: VereditoFoto; size?: number }) {
  const cor = VEREDITO_FOTO[veredito].cor;
  if (veredito === 'boa') return <CheckCircle2 size={size} color={cor} />;
  if (veredito === 'refazer') return <XCircle size={size} color={cor} />;
  return <AlertTriangle size={size} color={cor} />;
}

interface Props {
  produtoId: number;
  fotoUrl: string | null;
  podeEditar: boolean;
  onFotoOtimizada?: (novaUrl: string) => void;
}

export function AvaliarFotoCatalogo({ produtoId, fotoUrl, podeEditar, onFotoOtimizada }: Props) {
  const toast = useToast();
  const [avaliacao, setAvaliacao] = useState<AvaliacaoFoto | null>(null);
  const [avaliando, setAvaliando] = useState(false);
  const [otimizando, setOtimizando] = useState(false);

  async function avaliar() {
    setAvaliando(true);
    try {
      const r = await api.post<AvaliacaoFoto>(`/api/produtos/${produtoId}/avaliar-foto`, { comIA: true });
      setAvaliacao(r);
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao avaliar a foto');
    } finally {
      setAvaliando(false);
    }
  }

  async function otimizar() {
    setOtimizando(true);
    try {
      const r = await api.post<{ foto_url: string; bytes_antes: number; bytes_depois: number }>(
        `/api/produtos/${produtoId}/otimizar-foto`,
        {},
      );
      const kbAntes = Math.round(r.bytes_antes / 1024);
      const kbDepois = Math.round(r.bytes_depois / 1024);
      toast.sucesso(`Foto otimizada — ${kbAntes} KB → ${kbDepois} KB, formato quadrado WebP`);
      onFotoOtimizada?.(r.foto_url);
      setAvaliacao(null); // resultado antigo nao vale mais — reavaliar se quiser
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao otimizar a foto');
    } finally {
      setOtimizando(false);
    }
  }

  const v = avaliacao?.veredito;
  const podeOtimizar = !!avaliacao?.tecnico?.otimizavel && v !== 'sem_foto';

  return (
    <div className="pt-1 w-full">
      <div className="flex flex-col gap-2 items-center">
        <button
          type="button"
          onClick={avaliar}
          disabled={!fotoUrl || avaliando || otimizando}
          title={fotoUrl ? 'Avalia a foto: parte tecnica + leitura visual por IA' : 'Cadastre uma foto primeiro'}
          className="inline-flex items-center justify-center gap-2 w-52 px-4 py-2 rounded-lg text-sm font-medium bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Camera size={16} />
          {avaliando ? 'Avaliando...' : 'Avaliar foto'}
        </button>
        {podeOtimizar && podeEditar && (
          <button
            type="button"
            onClick={otimizar}
            disabled={otimizando || avaliando}
            title="Deixa a foto quadrada 1200x1200 com fundo branco e converte pra WebP (mais leve)."
            className="inline-flex items-center justify-center gap-2 w-52 px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Wand2 size={16} />
            {otimizando ? 'Otimizando...' : 'Otimizar foto'}
          </button>
        )}
      </div>

      {!fotoUrl && (
        <p className="text-sm text-gray-500 italic mt-2">Este produto ainda nao tem foto cadastrada.</p>
      )}

      {avaliacao && v && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
          {/* Veredito */}
          <div className="flex items-center gap-2">
            <IconeVeredito veredito={v} size={18} />
            <span className="text-sm font-bold" style={{ color: VEREDITO_FOTO[v].cor }}>
              {VEREDITO_FOTO[v].rotulo}
            </span>
            {avaliacao.tecnico && (
              <span className="text-sm text-gray-500">
                · {avaliacao.tecnico.largura}×{avaliacao.tecnico.altura}px · {avaliacao.tecnico.formato} ·{' '}
                {Math.round(avaliacao.tecnico.bytes / 1024)} KB
              </span>
            )}
          </div>

          {/* Problemas tecnicos */}
          {avaliacao.tecnico && avaliacao.tecnico.problemas.length > 0 && (
            <ul className="text-sm text-gray-700 list-disc pl-5 space-y-0.5">
              {avaliacao.tecnico.problemas.map((p, i) => (
                <li key={`t${i}`}>{p}</li>
              ))}
            </ul>
          )}

          {/* Parecer da IA */}
          {avaliacao.visual ? (
            <div className="text-sm text-gray-700">
              <span className="font-semibold">IA (nota {avaliacao.visual.nota}/5):</span> {avaliacao.visual.parecer}
              {avaliacao.visual.problemas.length > 0 && (
                <ul className="list-disc pl-5 mt-0.5 space-y-0.5">
                  {avaliacao.visual.problemas.map((p, i) => (
                    <li key={`v${i}`}>{p}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">A leitura visual por IA nao respondeu — avaliacao so tecnica.</p>
          )}

          {/* Orientacao final */}
          {v === 'refazer' && (
            <p className="text-sm text-red-700 bg-red-50 rounded px-2 py-1">
              Esta foto <strong>nao esta apta</strong> para Instagram, Facebook e o catalogo — a resolucao ou a
              nitidez nao atende. Tire uma foto nova do produto, bem iluminada, de perto e com pelo menos 1000px.
            </p>
          )}
          {v === 'boa' && (
            <p className="text-sm text-green-700">
              A foto esta apta para Instagram, Facebook e o catalogo.
              {podeOtimizar && ' Se quiser, o botao Otimizar deixa o arquivo mais leve e quadrado.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
